import { useState } from "react";
import { MessageCircle, Send, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useWhatsappConfig } from "@/lib/api";
import { useLocale } from "@/context/locale-context";

export default function Contact() {
  const { t } = useLocale();
  const { data: whatsapp, isLoading: isLoadingWa } = useWhatsappConfig();

  const [form, setForm] = useState({
    name:    "",
    email:   "",
    subject: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const isValid = form.name.trim() && form.message.trim();

  const handleWhatsApp = () => {
    if (!whatsapp?.whatsappNumber) return;
const phone = whatsapp.whatsappNumber.replace(/[^0-9]/g, "");

    const text = [
      `Hola, soy *${form.name}*`,
      form.email   ? `📧 ${form.email}`       : null,
      form.subject ? `📌 Asunto: ${form.subject}` : null,
      `\n${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="border-b">
        <div className="container mx-auto px-4 md:px-8 py-10 md:py-14">
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            {t.contact?.eyebrow || "Contacto"}
          </p>
          <h1 className="text-4xl md:text-5xl font-serif leading-tight mb-3">
            {t.contact?.title || "Hablemos"}
          </h1>
          <p className="text-muted-foreground text-base max-w-xl leading-6">
            {t.contact?.subtitle || "¿Te interesa una obra o tienes alguna consulta? Escríbenos directamente por WhatsApp."}
          </p>
        </div>
      </div>

      {/* ── Contenido ──────────────────────────────────────────── */}
      <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 md:gap-16 max-w-5xl mx-auto items-start">

          {/* ── Info lateral ── */}
          <div>
            <div className="w-8 h-px bg-foreground mb-6" />
            <h2 className="text-xl md:text-2xl font-serif mb-4">
              {t.contact?.infoTitle || "Información de contacto"}
            </h2>
            <p className="text-sm text-muted-foreground leading-6 mb-8">
              {t.contact?.infoDesc || "Atendemos consultas sobre obras disponibles, precios, comisiones y envíos. Respuesta en menos de 24 horas."}
            </p>

            <div className="space-y-4">
              {/* WhatsApp dinámico */}
              <div className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">WhatsApp</p>
                  {isLoadingWa ? (
                    <Skeleton className="h-4 w-32" />
                  ) : (
                    <p className="text-sm font-medium">{whatsapp?.whatsappNumber || "—"}</p>
                  )}
                </div>
              </div>

              {/* Email estático (opcional) */}
              {t.contact?.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Email</p>
                    <p className="text-sm font-medium">{t.contact.email}</p>
                  </div>
                </div>
              )}

              {/* Ciudad */}
              {t.contact?.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Ubicación</p>
                    <p className="text-sm font-medium">{t.contact.location}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Horario */}
            <div className="mt-8 border-t pt-6">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                {t.contact?.hoursLabel || "Horario de atención"}
              </p>
              <p className="text-sm text-muted-foreground leading-6">
                {t.contact?.hours || "Lunes a viernes · 9:00 – 18:00"}
              </p>
            </div>
          </div>

          {/* ── Formulario ── */}
          <div className="border bg-card p-6 md:p-8">
            <div className="space-y-5">

              {/* Nombre */}
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t.contact?.nameLabel || "Nombre"} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t.contact?.namePlaceholder || "Tu nombre completo"}
                  className="rounded-none h-10 text-sm"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t.contact?.emailLabel || "Correo electrónico"}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t.contact?.emailPlaceholder || "tu@correo.com"}
                  className="rounded-none h-10 text-sm"
                />
              </div>

              {/* Asunto */}
              <div className="space-y-1.5">
                <Label htmlFor="subject" className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t.contact?.subjectLabel || "Asunto"}
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder={t.contact?.subjectPlaceholder || "¿En qué obra estás interesado?"}
                  className="rounded-none h-10 text-sm"
                />
              </div>

              {/* Mensaje */}
              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t.contact?.messageLabel || "Mensaje"} *
                </Label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t.contact?.messagePlaceholder || "Cuéntanos en qué podemos ayudarte..."}
                  className="w-full rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                />
              </div>

              {/* Botón WhatsApp */}
              {isLoadingWa ? (
                <Skeleton className="h-11 w-full rounded-none" />
              ) : (
                <Button
                  onClick={handleWhatsApp}
                  disabled={!isValid || !whatsapp?.whatsappNumber}
                  className="w-full rounded-none h-11 uppercase tracking-widest text-xs gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.contact?.sendWhatsapp || "Enviar por WhatsApp"}
                </Button>
              )}

              <p className="text-[10px] text-muted-foreground text-center leading-4">
                {t.contact?.disclaimer || "Al hacer clic se abrirá WhatsApp con tu mensaje listo para enviar."}
              </p>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}